/**
 * ReadingHeatmap — GitHub-style contribution calendar of the days a
 * reader was actively reading, coloured by pages finished per day.
 *
 * • Data source: GET /api/user/reading-heatmap?days=365 (see backend
 *   routes/reading_events.py). A day counts when time_on_page >= 15s
 *   with the tab focused — same threshold as the verified-progress
 *   engine, so the heatmap never disagrees with the "verified %" bar.
 *
 * • Layout: 53 weeks × 7 days. Weeks arranged left→right (oldest→
 *   newest), days top→bottom (Sun→Sat). Compact 12px squares with a
 *   2px gap; month labels above, weekday labels on the left column.
 *   Legend at the bottom-right ranks the five color buckets.
 *
 * • Tooltip: pure CSS via native title attribute — no external tooltip
 *   library, no positioning bugs on scroll or window resize. Format:
 *   "12 pages · 34 min · Tue, Jul 15".
 *
 * • Empty state: renders the grid entirely in the "0 pages" grey so
 *   a new reader still sees the layout and understands what it will
 *   fill in with. Small text below explains the mechanic.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Flame, CalendarDays } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Five colour buckets, tuned to feel like GitHub Dark. Cell 0 is a soft
// grey so empty days still read as part of the calendar (not missing).
const COLOR_BUCKETS = [
  'rgba(255,255,255,0.05)', // 0 pages — inactive day
  'rgba(244,63,94,0.28)',   // 1-2 pages   — light rose
  'rgba(244,63,94,0.5)',    // 3-6 pages   — medium
  'rgba(244,63,94,0.75)',   // 7-14 pages  — strong
  'rgba(217,70,239,0.95)',  // 15+ pages   — fuchsia (heaviest)
];

const bucketFor = (pages, maxPages) => {
  if (!pages || pages <= 0) return 0;
  // Fixed thresholds work well for typical readers; the maxPages field
  // is used only to widen the top bucket ceiling in the legend.
  if (pages >= 15) return 4;
  if (pages >= 7) return 3;
  if (pages >= 3) return 2;
  return 1;
};

// Local YYYY-MM-DD string (UTC to match backend $dateToString "UTC").
const toISODate = (d) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ReadingHeatmap = ({ days = 365 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/user/reading-heatmap?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  // Build the full 53-week × 7-day grid. Column 0 = the week containing
  // (today - 364 days). Rows = Sun..Sat. Missing days = zero-count cells.
  const grid = useMemo(() => {
    if (!data) return null;
    const byDate = new Map();
    (data.days || []).forEach((d) => byDate.set(d.date, d));

    // End on today (UTC to match backend bucketing), walk backwards.
    const end = new Date();
    end.setUTCHours(0, 0, 0, 0);
    // Align to end of week (Saturday) — GitHub does the same so the
    // rightmost column contains today.
    const daysToSat = 6 - end.getUTCDay();
    const rightEdge = new Date(end);
    rightEdge.setUTCDate(rightEdge.getUTCDate() + daysToSat);

    // 53 weeks × 7 days = 371 cells. Start = rightEdge - 370 days.
    const cells = [];
    const start = new Date(rightEdge);
    start.setUTCDate(start.getUTCDate() - 370);

    for (let w = 0; w < 53; w += 1) {
      const week = [];
      for (let d = 0; d < 7; d += 1) {
        const cellDate = new Date(start);
        cellDate.setUTCDate(cellDate.getUTCDate() + w * 7 + d);
        // Skip cells that are in the future (rightEdge extends past today
        // to align the grid) — render them as null (invisible spacers).
        const isFuture = cellDate > end;
        const iso = toISODate(cellDate);
        const hit = byDate.get(iso);
        week.push({
          date: cellDate,
          iso,
          pages: hit ? hit.pages : 0,
          minutes: hit ? hit.minutes : 0,
          bucket: bucketFor(hit ? hit.pages : 0, data.max_pages_in_day || 0),
          isFuture,
        });
      }
      cells.push(week);
    }
    return { weeks: cells, start, end: rightEdge };
  }, [data]);

  // Where to draw each month label on the top row. Only draw when a
  // new month first appears in that column's Sunday cell.
  const monthColumns = useMemo(() => {
    if (!grid) return [];
    const cols = [];
    let lastMonth = -1;
    grid.weeks.forEach((week, w) => {
      const first = week[0].date;
      const m = first.getUTCMonth();
      if (m !== lastMonth) {
        cols.push({ w, label: MONTH_LABELS[m] });
        lastMonth = m;
      }
    });
    return cols;
  }, [grid]);

  if (loading) {
    return (
      <div
        className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6 mb-8 animate-pulse"
        data-testid="reading-heatmap-loading"
      >
        <div className="h-4 w-40 bg-white/10 rounded mb-4" />
        <div className="h-24 bg-white/5 rounded" />
      </div>
    );
  }

  if (!data) {
    // Silent fail — some users (fresh accounts) may not have reading_events
    // yet, and we don't want to nag them. Simply don't render.
    return null;
  }

  const totals = data.totals || { pages: 0, active_days: 0, minutes: 0 };

  return (
    <div
      className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6 mb-8"
      data-testid="reading-heatmap"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-fuchsia-400" />
          <h3 className="text-white font-semibold text-base sm:text-lg">Reading Activity</h3>
          <span className="text-xs text-gray-500 hidden sm:inline">
            · last {days} days
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <Flame className="h-3.5 w-3.5 text-rose-400" />
            <span className="text-white font-semibold" data-testid="heatmap-active-days">
              {totals.active_days}
            </span>
            <span className="text-gray-400">active days</span>
          </div>
          <div className="text-gray-400">
            <span className="text-white font-semibold" data-testid="heatmap-total-pages">
              {totals.pages}
            </span>
            <span> pages</span>
          </div>
          <div className="text-gray-400 hidden sm:block">
            <span className="text-white font-semibold">{Math.round(totals.minutes)}</span>
            <span> min</span>
          </div>
        </div>
      </div>

      {/* Scroll container so on very narrow screens the whole grid can
          slide horizontally without breaking layout. */}
      <div className="overflow-x-auto pb-1">
        <div className="inline-block min-w-full">
          {/* Month labels row */}
          <div className="flex" style={{ marginLeft: 26 }}>
            {grid && grid.weeks.map((_, w) => {
              const label = monthColumns.find((c) => c.w === w);
              return (
                <div
                  key={`m-${w}`}
                  className="text-[10px] text-gray-500"
                  style={{ width: 14, height: 14 }}
                >
                  {label ? label.label : ''}
                </div>
              );
            })}
          </div>

          <div className="flex">
            {/* Weekday labels column */}
            <div className="flex flex-col mr-1.5 pt-0.5">
              {WEEKDAY_LABELS.map((wd, i) => (
                <div
                  key={wd}
                  className="text-[10px] text-gray-500 leading-none"
                  style={{ height: 14, marginBottom: 2 }}
                >
                  {/* Show Mon/Wed/Fri only to save space */}
                  {i % 2 === 1 ? wd : ''}
                </div>
              ))}
            </div>

            {/* The grid itself */}
            <div className="flex">
              {grid && grid.weeks.map((week, w) => (
                <div key={`w-${w}`} className="flex flex-col" style={{ marginRight: 2 }}>
                  {week.map((cell) => {
                    if (cell.isFuture) {
                      return <div key={cell.iso} style={{ width: 12, height: 12, marginBottom: 2 }} />;
                    }
                    const nice = cell.date.toLocaleDateString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric',
                    });
                    const tip = cell.pages
                      ? `${cell.pages} page${cell.pages === 1 ? '' : 's'} · ${Math.round(cell.minutes)} min · ${nice}`
                      : `No reading · ${nice}`;
                    return (
                      <div
                        key={cell.iso}
                        title={tip}
                        aria-label={tip}
                        data-testid={`heatmap-cell-${cell.iso}`}
                        style={{
                          width: 12,
                          height: 12,
                          marginBottom: 2,
                          borderRadius: 2,
                          background: COLOR_BUCKETS[cell.bucket],
                          border: cell.bucket === 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          cursor: cell.pages ? 'pointer' : 'default',
                          transition: 'transform 120ms ease-out',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.35)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <p className="text-[11px] text-gray-500">
          Each square is a day — brighter means more pages read that day.
        </p>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span>Less</span>
          {COLOR_BUCKETS.map((c, i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: c,
                border: i === 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}
              data-testid={`heatmap-legend-${i}`}
            />
          ))}
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default ReadingHeatmap;
